import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Code2, Layers, Zap, Brain, RefreshCw, CheckCircle, ArrowRight, Users } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';

const iconMap = { Globe, Code2, Layers, Zap, Brain, RefreshCw };

function PageHero({ tr }) {
  return (
    <section
      data-testid="services-hero"
      className="pt-32 pb-16 relative"
      style={{ backgroundColor: '#0D1F1F' }}
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: 'radial-gradient(circle at 60% 40%, rgba(79,142,247,0.4) 0%, transparent 50%)' }}
      />
      <div className="section-container relative text-center max-w-3xl mx-auto">
        <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
          {tr.servicesPage.hero.title}
        </h1>
        <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
        <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
          {tr.servicesPage.hero.subtitle}
        </p>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const [ref, visible] = useScrollReveal();

  return (
    <main data-testid="services-page">
      <PageHero tr={tr} />

      {/* Services list */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div className="space-y-16">
            {tr.servicesPage.items.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Globe;
              const isEven = i % 2 === 0;
              return (
                <ServiceCard key={i} svc={svc} Icon={Icon} isEven={isEven} index={i} tr={tr} />
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
        <div className="section-container text-center max-w-xl mx-auto" ref={ref}>
          <div className={`reveal ${visible ? 'visible' : ''}`}>
            <h2 className="font-sora font-bold text-3xl text-white mb-4">
              {lang === 'fr' ? 'Un projet en tête ?' : 'Have a project in mind?'}
            </h2>
            <p className="text-gray-300 font-inter mb-8">
              {lang === 'fr'
                ? 'Discutons de vos besoins et trouvons la solution idéale pour votre entreprise.'
                : 'Let\'s discuss your needs and find the ideal solution for your business.'}
            </p>
            <Link to="/devis" data-testid="services-cta" className="btn-primary">
              {tr.nav.quote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function ServiceCard({ svc, Icon, isEven, index, tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      data-testid={`service-detail-${index}`}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center reveal ${visible ? 'visible' : ''}`}
    >
      {/* Text content - alternates side */}
      <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
        <div
          className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-xl"
          style={{ background: 'rgba(79,142,247,0.1)', border: '1px solid rgba(79,142,247,0.15)' }}
        >
          <Icon size={20} className="text-exevori-primary" strokeWidth={1.5} />
          <span className="gradient-text font-sora font-semibold text-sm">{svc.subtitle}</span>
        </div>
        <h2 className="font-sora font-bold text-3xl text-white mb-4">{svc.title}</h2>
        <p className="text-gray-300 font-inter leading-relaxed mb-6">{svc.desc}</p>

        {/* Benefits */}
        <div className="space-y-3 mb-8">
          {svc.benefits.map((b, j) => (
            <div key={j} className="flex items-center gap-3">
              <CheckCircle size={16} className="text-exevori-primary flex-shrink-0" />
              <span className="text-gray-300 text-sm font-inter">{b}</span>
            </div>
          ))}
        </div>

        {/* Clients */}
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-xl mb-6"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Users size={14} className="text-gray-400 flex-shrink-0" />
          <span className="text-gray-400 text-sm font-inter">{svc.clients}</span>
        </div>

        <Link to="/devis" data-testid={`service-${index}-cta`} className="btn-primary">
          {tr.nav.quote} <ArrowRight size={16} />
        </Link>
      </div>

      {/* Visual side */}
      <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
        <div className="exevori-card p-10 flex items-center justify-center min-h-64 relative overflow-hidden group">
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
            style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
          />
          <div className="text-center">
            <div
              className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
              style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.15), rgba(155,89,245,0.15))' }}
            >
              <Icon size={48} className="text-exevori-primary" strokeWidth={1} />
            </div>
            <div className="font-sora font-bold text-xl text-white mb-2">{svc.title}</div>
            <div
              className="inline-block px-3 py-1 rounded-full text-xs font-inter"
              style={{ background: 'linear-gradient(to right, #4F8EF7, #9B59F5)', color: 'white' }}
            >
              Exevori
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
